/**
 * The words the console uses for how an edge device is doing (release 3.1,
 * fleet wave 1). ONE vocabulary on every screen, the same one the controller
 * computes (apps/api-controller/src/modules/edge/fleet-health.ts):
 *
 *   Healthy         checked in within its window, no fault
 *   Unhealthy       checks in, but is not working (the reason says how)
 *   Offline         no check-in within its window
 *   Inactive        no check-in for much longer
 *   Pending         enrolled, never checked in
 *   Decommissioned  decommissioned by an operator
 *
 * The windows depend on the kind of agent, because a laptop sleeps every night
 * and a server-side scanner should always be on: a laptop is silent after
 * 24 h and down after 7 days; a server-side agent is silent after 2 missed
 * check-ins and down after 3 missed windows.
 *
 * Pure (no React, no icons) so the pickers and forms that only need a word can
 * import it without pulling in the Edge Fleet page.
 */
import type { EdgeAgentClass, EdgeDegradedReason, EdgeHealthReason, EdgeHealthStatus } from './api';

export const EDGE_HEALTH_STATES: readonly EdgeHealthStatus[] = [
  'healthy',
  'degraded',
  'silent',
  'down',
  'pending',
  'decommissioned',
];

export const EDGE_HEALTH_LABEL: Record<EdgeHealthStatus, string> = {
  healthy: 'Healthy',
  degraded: 'Unhealthy',
  silent: 'Offline',
  down: 'Inactive',
  pending: 'Pending',
  decommissioned: 'Decommissioned',
};

/** One-line meaning of each state, for any kind of agent. */
export const EDGE_HEALTH_HINT: Record<EdgeHealthStatus, string> = {
  healthy: 'checked in within its window, no fault',
  degraded: 'online, but not working (the reason says why)',
  silent: 'no check-in within its window (laptops: over 24 h; server-side agents: 2 missed check-ins)',
  down: 'no check-in for much longer (laptops: over 7 days; server-side agents: 3 missed windows)',
  pending: 'installed, never checked in',
  decommissioned: 'decommissioned by an operator',
};

export const EDGE_HEALTH_VARIANT: Record<EdgeHealthStatus, 'success' | 'warning' | 'critical' | 'glass'> = {
  healthy: 'success',
  degraded: 'warning',
  silent: 'warning',
  down: 'critical',
  pending: 'glass',
  decommissioned: 'glass',
};

/** Why a device that checks in is degraded. */
export const EDGE_HEALTH_REASON_LABEL: Record<EdgeHealthReason, string> = {
  impaired: 'checking in, doing no work',
  never_worked: 'has never finished a scan',
  agent_reported: 'the agent reports a fault',
};

export const EDGE_AGENT_CLASS_LABEL: Record<EdgeAgentClass, string> = {
  endpoint: 'Laptop agent',
  vpcPod: 'Server-side agent',
  gateway: 'Gateway',
  // Release 3.1 wave 2a: the DSAR connector and agentless collection, which
  // are not licensed per device.
  service: 'Service (not metered)',
};

function isKnownState(s: string): s is EdgeHealthStatus {
  return (EDGE_HEALTH_STATES as readonly string[]).includes(s);
}

/** The label for a state; a word this build does not know is shown as sent, never as blank. */
export function edgeHealthLabel(status: string): string {
  return isKnownState(status) ? EDGE_HEALTH_LABEL[status] : status;
}

/** The label for a reason, or null when there is none or it is unknown. */
export function edgeHealthReasonLabel(reason: string | null | undefined): string | null {
  if (!reason) return null;
  return Object.hasOwn(EDGE_HEALTH_REASON_LABEL, reason)
    ? EDGE_HEALTH_REASON_LABEL[reason as EdgeHealthReason]
    : reason.replace(/_/g, ' ');
}

/**
 * What a state means for THIS device: the thresholds of its own kind of agent
 * where they differ, and the reason when it is degraded.
 */
export function edgeHealthHint(
  status: string,
  agentClass?: EdgeAgentClass | null,
  reason?: string | null,
): string {
  if (status === 'degraded') return edgeHealthReasonLabel(reason) ?? EDGE_HEALTH_HINT.degraded;
  const laptop = agentClass === 'endpoint';
  const known = agentClass === 'endpoint' || agentClass === 'vpcPod' || agentClass === 'gateway';
  if (status === 'silent' && known) {
    return laptop ? 'no check-in for over 24 h' : 'missed 2 check-ins';
  }
  if (status === 'down' && known) {
    return laptop ? 'no check-in for over 7 days' : 'missed 3 check-in windows';
  }
  return isKnownState(status) ? EDGE_HEALTH_HINT[status] : 'a state this console does not know';
}

/** "every 5 min", "every 4 h", "every 30 s"; "not reported" when the agent declared none. */
export function checkInCadence(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds) || seconds <= 0) {
    return 'not reported';
  }
  if (seconds < 60) return `every ${Math.round(seconds)} s`;
  if (seconds < 3600) {
    const m = seconds / 60;
    return `every ${Number.isInteger(m) ? m : m.toFixed(1)} min`;
  }
  if (seconds < 86_400) {
    const h = seconds / 3600;
    return `every ${Number.isInteger(h) ? h : h.toFixed(1)} h`;
  }
  const d = seconds / 86_400;
  return `every ${Number.isInteger(d) ? d : d.toFixed(1)} day${d === 1 ? '' : 's'}`;
}

// ---- Why Degraded (release 3.3, R3-OI-184) ---------------------------------
//
// The agent sends a specific reason with its heartbeat (coverage_partial,
// findings_refused, scan_overdue, ...); every screen used to say only "the
// agent reports a fault". The controller now returns the reason, in these
// words, with the next step (EdgePodSummary.degradedReason). The same
// vocabulary is here for the filter list and for a controller older than
// release 3.3, which sends the reason only inside statusDetail. It must equal
// the controller's apps/api-controller/src/modules/edge/degraded-reasons.ts
// (edge-degraded-reason.test.ts compares the two).

export interface EdgeDegradedReasonText {
  label: string;
  nextStep: string;
}

export const EDGE_DEGRADED_REASONS: Readonly<Record<string, EdgeDegradedReasonText>> = {
  // The controller's own verdicts.
  impaired: {
    label: 'checking in, doing no work',
    nextStep: 'Look in the device drawer for a pause, Scan hours that never open, or a scan that stops at once.',
  },
  never_worked: {
    label: 'has never finished a scan',
    nextStep: 'Wait for its first scheduled scan or start one with Rescan now; at a low CPU ceiling a first laptop scan can take hours.',
  },
  agent_reported: {
    label: 'the agent reports a fault',
    nextStep: "Read the agent's own words in the device drawer (Actually running).",
  },
  // The agents' verdicts on their own last run.
  coverage_partial: {
    label: 'some folders were only partly scanned',
    nextStep: 'Files were skipped (locked, too large or unreadable); the drawer shows them under What the last scan did not read. Nothing to do unless those files matter.',
  },
  findings_refused: {
    label: 'the console refused some of its findings',
    nextStep: "Read the refusal in the agent's words below; tell FOCTTA if it is not a limit you set.",
  },
  findings_dropped: {
    label: 'the agent dropped some findings before sending them',
    nextStep: 'The run went over its findings limit; narrow the scan folders or raise the limit.',
  },
  findings_not_delivered: {
    label: 'its findings did not reach the console',
    nextStep: 'The device checks in, so look for a proxy or firewall that blocks larger uploads; they are sent again on the next scan.',
  },
  findings_partly_delivered: {
    label: 'some findings did not reach the console',
    nextStep: 'Usually a network drop during the upload; the rest are sent again on the next scan. Use Request log (drawer, Logs) if it repeats.',
  },
  scan_overdue: {
    label: 'no scan has finished for longer than its schedule',
    nextStep: 'The laptop is off, asleep or outside its scan hours most of the time: check its Scan hours (drawer) against when it is used.',
  },
  run_duration_regression: {
    label: 'the last scan took much longer than usual',
    nextStep: 'Check for a newly added large folder or a lower CPU ceiling; nothing is broken.',
  },
  run_aborted: {
    label: 'the last scan stopped part-way',
    nextStep: "Read the cause in the agent's words below; use Request log (drawer, Logs) and send it to FOCTTA if it repeats.",
  },
  targets_failed: {
    label: 'some scan folders failed',
    nextStep: 'The agent names each failed folder and why below (a missing path or no permission); fix the folder list or the permission.',
  },
  all_targets_failed: {
    label: 'every scan folder failed',
    nextStep: 'The agent names each failed folder and why below (a missing path or no permission); fix the folder list or the permission.',
  },
  nothing_attempted: {
    label: 'the last scan had nothing to scan',
    nextStep: "Check that the device's scan folders exist on it and are not all excluded.",
  },
  no_scan_recorded: {
    label: 'no scan has finished on this install yet',
    nextStep: 'Wait for its first scheduled scan or start one with Rescan now; at a low CPU ceiling a first laptop scan can take hours.',
  },
  health_unknown: {
    label: 'the agent cannot tell when it last scanned',
    nextStep: "Check the device's clock and time zone; the next finished scan clears this.",
  },
};
const UNKNOWN_REASON_NEXT_STEP = "Read the agent's own words below; use Request log (drawer, Logs) and send it to FOCTTA if the reason is not clear.";

/** The words for a reason code; a code this console does not know reads as its own words. */
export function edgeDegradedReasonText(code: string): EdgeDegradedReasonText {
  if (Object.hasOwn(EDGE_DEGRADED_REASONS, code)) return EDGE_DEGRADED_REASONS[code] as EdgeDegradedReasonText;
  return { label: code.replace(/_/g, ' '), nextStep: UNKNOWN_REASON_NEXT_STEP };
}

/**
 * Why this device is Degraded, or null when it is not: the controller's answer
 * when it sent one, else read from statusDetail ("<code>: <sentence>") the
 * way the controller does it.
 */
export function edgeDegradedReason(d: {
  healthStatus: string;
  healthReason?: string | null;
  statusDetail?: string | null;
  degradedReason?: EdgeDegradedReason | null;
}): EdgeDegradedReason | null {
  if (d.degradedReason !== undefined) return d.degradedReason;
  if (d.healthStatus !== 'degraded') return null;
  if (d.healthReason === 'agent_reported') {
    // Not trimmed, exactly as the controller reads it (degraded-reasons.ts).
    const m = /^([a-z][a-z0-9_]{1,63})(?::\s*([\s\S]*))?$/.exec(d.statusDetail ?? '');
    if (m && m[1]) {
      const detail = m[2]?.trim();
      return { code: m[1], ...edgeDegradedReasonText(m[1]), detail: detail ? detail : null, source: 'agent' };
    }
    return { code: 'agent_reported', ...edgeDegradedReasonText('agent_reported'), detail: null, source: 'agent' };
  }
  const code = d.healthReason && /^[a-z][a-z0-9_]{1,63}$/.test(d.healthReason) ? d.healthReason : 'agent_reported';
  return { code, ...edgeDegradedReasonText(code), detail: null, source: 'controller' };
}

/** The reasons an operator can filter by, A-Z by their words. */
export function edgeDegradedReasonOptions(): Array<{ code: string; label: string }> {
  return Object.entries(EDGE_DEGRADED_REASONS)
    .map(([code, t]) => ({ code, label: t.label }))
    .sort((a, b) => a.label.localeCompare(b.label));
}
